import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LocationProvider } from "@/contexts/LocationContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import PropertiesPage from "@/pages/PropertiesPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import MapSearchPage from "@/pages/MapSearchPage";
import CityLandingPage from "@/pages/CityLandingPage";
import LocalityLandingPage from "@/pages/LocalityLandingPage";
import DashboardPage from "@/pages/DashboardPage";
import AddPropertyPage from "@/pages/AddPropertyPage";
import EMICalculatorPage from "@/pages/EMICalculatorPage";
import ComparePage from "@/pages/ComparePage";
import MessagesPage from "@/pages/MessagesPage";
import ProfilePage from "@/pages/ProfilePage";
import PremiumPage from "@/pages/PremiumPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import AccountDeletionPage from "@/pages/AccountDeletionPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import RefundPolicyPage from "@/pages/RefundPolicyPage";
import ShippingPolicyPage from "@/pages/ShippingPolicyPage";
import GrievancePage from "@/pages/GrievancePage";
import PropertyMatchQuizPage from "@/pages/PropertyMatchQuizPage";
import KnowledgeQuizPage from "@/pages/KnowledgeQuizPage";
import AffordabilityCalculatorPage from "@/pages/AffordabilityCalculatorPage";
import StampDutyCalculatorPage from "@/pages/StampDutyCalculatorPage";
import RentalYieldCalculatorPage from "@/pages/RentalYieldCalculatorPage";
import MarketTrendsPage from "@/pages/MarketTrendsPage";
import GlossaryPage from "@/pages/GlossaryPage";
import AboutUsPage from "@/pages/AboutUsPage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceCategoryPage from "@/pages/ServiceCategoryPage";
import ServiceCheckoutPage from "@/pages/ServiceCheckoutPage";
import ServiceBookingSuccessPage from "@/pages/ServiceBookingSuccessPage";
import PackersMoversPage from "@/pages/PackersMoversPage";
import BecomeAProPage from "@/pages/BecomeAProPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import ProfessionalsPage from "@/pages/ProfessionalsPage";
import BecomeAPartnerPage from "@/pages/BecomeAPartnerPage";
import PartnerDashboardPage from "@/pages/PartnerDashboardPage";

import { ServiceCartProvider } from "@/contexts/ServiceCartContext";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import BharatAIChat from "@/components/BharatAIChat";
import FoundersPopup from "@/components/FoundersPopup";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F172A]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Home page has its own full-bleed hero, all other pages get a global spacer to clear the fixed navbar
  const isHome = location.pathname === '/';
  // City landing pages also have a full-bleed dark hero that sits under the navbar
  const isFullBleed = isHome || location.pathname.startsWith('/real-estate/');
  // Matches navbar height exactly: 72px / 80px (mobile / lg)
  const mainPadding = isFullBleed ? '' : 'pt-[72px] lg:pt-20';

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F9F7]">
      <ScrollToTop />
      <Navbar />
      <main className={`flex-1 ${mainPadding}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/properties" element={<PropertiesPage />} />
          <Route path="/properties/:id" element={<PropertyDetailPage />} />
          <Route path="/map-search" element={<MapSearchPage />} />
          <Route path="/real-estate/:citySlug" element={<CityLandingPage />} />
          <Route path="/real-estate/:citySlug/:localitySlug" element={<LocalityLandingPage />} />
          <Route path="/emi-calculator" element={<EMICalculatorPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/profile/:userId" element={<ProfilePage />} />

          {/* Premium & Payments */}
          <Route path="/premium" element={<PremiumPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />

          {/* Legal */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
          <Route path="/delete-account" element={<AccountDeletionPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
          <Route path="/grievance" element={<GrievancePage />} />

          {/* Tools & Insights */}
          <Route path="/quiz" element={<PropertyMatchQuizPage />} />
          <Route path="/knowledge-quiz" element={<KnowledgeQuizPage />} />
          <Route path="/affordability-calculator" element={<AffordabilityCalculatorPage />} />
          <Route path="/stamp-duty-calculator" element={<StampDutyCalculatorPage />} />
          <Route path="/rental-yield-calculator" element={<RentalYieldCalculatorPage />} />
          <Route path="/market-trends" element={<MarketTrendsPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/about" element={<AboutUsPage />} />

          {/* Services & Movers */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/checkout" element={<ProtectedRoute><ServiceCheckoutPage /></ProtectedRoute>} />
          <Route path="/services/booking-success" element={<ProtectedRoute><ServiceBookingSuccessPage /></ProtectedRoute>} />
          <Route path="/services/:slug" element={<ServiceCategoryPage />} />
          <Route path="/packers-movers" element={<PackersMoversPage />} />
          <Route path="/become-a-pro" element={<BecomeAProPage />} />

          {/* Professional Services Directory */}
          <Route path="/professionals" element={<ProfessionalsPage />} />
          <Route path="/professionals/:slug" element={<ProfessionalsPage />} />
          <Route path="/become-a-partner" element={<BecomeAPartnerPage />} />
          <Route path="/partner" element={<ProtectedRoute><PartnerDashboardPage /></ProtectedRoute>} />

          {/* Help Center */}
          <Route path="/help-center" element={<HelpCenterPage />} />
          <Route path="/help-center/:slug" element={<HelpCenterPage />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute><DashboardPage /></ProtectedRoute>
          } />
          <Route path="/add-property" element={
            <ProtectedRoute><AddPropertyPage /></ProtectedRoute>
          } />
          <Route path="/edit-property/:id" element={
            <ProtectedRoute><AddPropertyPage /></ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute><MessagesPage /></ProtectedRoute>
          } />
          <Route path="/messages/:userId" element={
            <ProtectedRoute><MessagesPage /></ProtectedRoute>
          } />
        </Routes>
      </main>
      <Footer />
      <BharatAIChat />
      <FoundersPopup />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LocationProvider>
        <ChatProvider>
          <ServiceCartProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </ServiceCartProvider>
        </ChatProvider>
        </LocationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
